import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {Handle, Position, NodeProps} from 'reactflow';

import {PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as Colors from 'ui-library/Colors';

import {AnalysisGraphNodeData} from './analysis-graph/AnalysisGraphTypes';
import {
  ANALYSIS_FLOWCHART_NODE_COLOR,
  ANALYSIS_FLOWCHART_NODE_ERROR_COLOR,
  ANALYSIS_FLOWCHART_NODE_HEIGHT,
  ANALYSIS_FLOWCHART_NODE_WIDTH,
} from './AnalysisFlowchartStyleConstants';
import StepNodeContents from './node-contents/StepNodeContents';
import StepPopover from './popovers/StepPopover';

/**
 * Calculate an offset from center for a handle, so handles are evenly distributed along the node's edge
 */
function calculateHandleOffset(index: number, numStepDatas: number): number {
  return (
    ANALYSIS_FLOWCHART_NODE_WIDTH / 2 - (index + 1) * (ANALYSIS_FLOWCHART_NODE_WIDTH / (numStepDatas + 1))
  );
}

interface HandlesProps {
  handleIds: Array<string>;
  type: 'target' | 'source';
}

function Handles({handleIds, type}: HandlesProps): JSX.Element {
  return (
    <>
      {handleIds.map((handleId, index) => (
        <Handle
          key={handleId}
          id={handleId}
          position={type === 'target' ? Position.Top : Position.Bottom}
          style={{transform: `translateX(${calculateHandleOffset(index, handleIds.length)}px)`}}
          type={type}
        />
      ))}
    </>
  );
}

export default function AnalysisFlowchartNode({
  data,
}: Pick<NodeProps<AnalysisGraphNodeData>, 'data' | 'id'>): JSX.Element {
  const isFailed = data.step.statusCode === PipelineStepStatusCode.FAILED;

  return (
    <StepPopover step={data.step}>
      <div className={css(styles.nodeContainer, isFailed && styles.failed)}>
        <Handles handleIds={data.targetHandleIds} type="target" />
        <StepNodeContents {...data} />
        <Handles handleIds={data.sourceHandleIds} type="source" />
      </div>
    </StepPopover>
  );
}

const styles = StyleSheet.create({
  failed: {
    backgroundColor: ANALYSIS_FLOWCHART_NODE_ERROR_COLOR,
    color: Colors.WHITE,
  },
  nodeContainer: {
    backgroundColor: ANALYSIS_FLOWCHART_NODE_COLOR,
    border: `1px solid ${Colors.NEAR_BLACK}`,
    borderRadius: 4,
    height: ANALYSIS_FLOWCHART_NODE_HEIGHT,
    width: ANALYSIS_FLOWCHART_NODE_WIDTH,
    padding: 5,
    overflow: 'hidden',
    textAlign: 'center',
  },
});
